import { ComponentType } from '@angular/cdk/portal';
import { NgComponentOutlet } from '@angular/common';
import {
  Component,
  Input,
  OnDestroy,
  OnInit,
  effect,
  inject,
  input,
} from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { PageEvent } from '@angular/material/paginator';
import {
  MatCell,
  MatCellDef,
  MatColumnDef,
  MatHeaderCell,
  MatHeaderCellDef,
  MatHeaderRow,
  MatHeaderRowDef,
  MatRow,
  MatRowDef,
  MatTable,
  MatTableDataSource,
} from '@angular/material/table';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { firstValueFrom, Subject, takeUntil } from 'rxjs';
import { PayeeInfoComponent } from '../payee-info/payee-info.component';
import { WorkflowService } from '../services/workflow/workflow.service';
import { CaseClientFilterComponent } from './case-client-filter/case-client-filter.component';
import { CaseDebtorNameFilterComponent } from './case-debtor-name-filter/case-debtor-name-filter.component';
import { CaseProductFilterComponent } from './case-product-filter/case-product-filter.component';
import { CaseQuickFiltersComponent } from './case-quick-filters/case-quick-filters.component';
import { CaseReferenceIdFilterComponent } from './case-reference-id-filter/case-reference-id-filter.component';
import { CaseStageFilterComponent } from './case-stage-filter/case-stage-filter.component';
import { CaseStartedAtFilterComponent } from './case-started-at-filter/case-started-at-filter.component';
import { CaseStateFilterComponent } from './case-state-filter/case-state-filter.component';
import { CaseTransitionFilterComponent } from './case-transition-filter/case-transition-filter.component';
import {
  AdditionalAction,
  AdditionalColumn,
  CasesTableComponent,
} from './cases-table/cases-table.component';
import { ClearFiltersComponent } from './clear-filters/clear-filters.component';
import { CofaceCasesService } from './coface-cases.service';
import { PayeeCasesService } from './payee-cases.service';
import {
  StartPaymentChoiceDialogComponent,
  StartPaymentChoiceDialogData,
} from './start-payment-choice-dialog/start-payment-choice-dialog.component';
import { CaseTableItem, CaseTableItemAction, ClientOption, Pagination, Stage } from './types';

export { UNKNOWN_CLIENT_NAME } from './types';

@Component({
  selector: 'payee-cases',
  templateUrl: './payee-cases.component.html',
  styleUrls: ['./payee-cases.component.scss'],
  imports: [
    CaseQuickFiltersComponent,
    CaseReferenceIdFilterComponent,
    CaseDebtorNameFilterComponent,
    CaseProductFilterComponent,
    CaseStageFilterComponent,
    CaseStateFilterComponent,
    CaseStartedAtFilterComponent,
    CaseTransitionFilterComponent,
    CaseClientFilterComponent,
    NgComponentOutlet,
    ClearFiltersComponent,
    MatIconModule,
    PayeeInfoComponent,
    CasesTableComponent,
    MatTable,
    MatColumnDef,
    MatHeaderCellDef,
    MatHeaderCell,
    MatCellDef,
    MatCell,
    MatHeaderRowDef,
    MatHeaderRow,
    MatRowDef,
    MatRow,
    TranslateModule,
  ],
})
export class PayeeCasesComponent implements OnInit, OnDestroy {
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private cofaceCasesService = inject(CofaceCasesService);
  private payeeCasesService = inject(PayeeCasesService);
  private workflowService = inject(WorkflowService);
  private dialog = inject(MatDialog);

  private readonly destroy = new Subject<void>();

  @Input() allowEdit = true;
  @Input() allowDelete = true;
  @Input() showTodoSubTabs = false;
  readonly showClientColumn = input(true);
  readonly showPartnerColumn = input(true);

  @Input() stages: Stage[] = ['draft', 'ongoing', 'closed'];

  @Input() additionalFilters: ComponentType<any>[] = [];
  @Input() additionalActions: AdditionalAction[] = [];
  readonly additionalColumns = input<AdditionalColumn[]>([]);
  @Input() showInfo = true;
  @Input() showExport = false;

  @Input({ required: true }) searchClients!: (name: string) => Promise<ClientOption[]>;
  @Input({ required: true }) resolveClient!: (uuid: string) => Promise<ClientOption | null>;

  @Input({ required: true }) set getCasesPath(path: string | null) {
    this.payeeCasesService.getCasesPath.next(path);
  }

  @Input({ required: true }) set getDebtorsPath(path: string | null) {
    this.payeeCasesService.getDebtorsPath.next(path);
  }

  stateOptions: string[] = [];

  readonly dataSource = new MatTableDataSource<CaseTableItem>();
  readonly skeletonDataSource = new MatTableDataSource<any>([]);

  readonly loading = this.payeeCasesService.loading;
  readonly exporting = this.payeeCasesService.exporting;
  pagination?: Pagination;

  constructor() {
    effect(() => {
      if (this.payeeCasesService.loading()) {
        this.updateSkeletonDataSource();
      }
    });
  }

  get draftSelected(): boolean {
    return this.payeeCasesService.stage.value === 'draft';
  }
  get ongoingSelected(): boolean {
    return this.payeeCasesService.stage.value === 'ongoing';
  }
  get closedSelected(): boolean {
    return this.payeeCasesService.stage.value === 'closed';
  }

  get skeletonRowCount(): number {
    // Always show 10 skeleton rows
    return 10;
  }

  get skeletonColumnCount(): number {
    // Base columns: payee_case_reference_id, started_at, parties, client, state, partner, original_claim, payment_status (8)
    // + additional columns + actions (1)
    const showClient =
      this.showClientColumn() && !this.payeeCasesService.client.value;
    let baseColumns = showClient ? 8 : 7;
    if (!this.showPartnerColumn()) {
      baseColumns -= 1;
    }
    return baseColumns + this.additionalColumns().length + 1;
  }

  getSkeletonRowArray(): number[] {
    return Array(this.skeletonRowCount)
      .fill(0)
      .map((_, i) => i + 1);
  }

  getSkeletonColumnArray(): number[] {
    return Array(this.skeletonColumnCount)
      .fill(0)
      .map((_, i) => i + 1);
  }

  getSkeletonColumnDefs(): string[] {
    return this.getSkeletonColumnArray().map((_, i) => 'skeleton-col-' + i);
  }

  async ngOnInit() {
    this.payeeCasesService.resetFilters();

    this.payeeCasesService.cases
      .pipe(takeUntil(this.destroy))
      .subscribe((cases) => {
        this.pagination = cases.pagination;
        this.dataSource.data = cases.data;
        if (this.loading()) {
          this.updateSkeletonDataSource();
        }
      });

    this.payeeCasesService.onInit();

    this.payeeCasesService.filtersWithPagination
      .pipe(takeUntil(this.destroy))
      .subscribe(() => this.setFilterQueryParams());

    await this.openDialogFromQuery();
  }

  private async openDialogFromQuery(): Promise<void> {
    const params = this.route.snapshot.queryParams;
    if (!('open_dialog' in params)) {
      return;
    }

    const open_dialog: unknown = params.open_dialog;
    if (
      open_dialog !== 'full_payment'
      && open_dialog !== 'close'
      && open_dialog !== 'request_pro_forma'
      && open_dialog !== 'performance_claim_response'
    ) {
      return;
    }

    const payeeCaseReferenceId =
      params.payee_case_reference_id
      || this.payeeCasesService.payee_case_reference_id.value;
    const caseId = params.case_id;

    this.router.navigate([], {
      queryParams: { open_dialog: null, case_id: null },
      queryParamsHandling: 'merge',
    });

    if (open_dialog === 'request_pro_forma') {
      if (caseId) {
        const response = await firstValueFrom(this.payeeCasesService.cases);
        const payeeCase = response.data.find((item) => item.id === caseId);
        if (payeeCase) {
          this.dialog.open<
            StartPaymentChoiceDialogComponent,
            StartPaymentChoiceDialogData
          >(StartPaymentChoiceDialogComponent, {
            data: {
              caseId,
              canPayWithCredit: payeeCase.can_pay_with_credit,
            },
            autoFocus: false,
          });
        }
      }
      return;
    }

    if (!payeeCaseReferenceId) {
      return;
    }

    if (open_dialog === 'performance_claim_response') {
      await this.openPerformanceClaimResponse(payeeCaseReferenceId);
      return;
    }

    try {
      let data;
      switch (open_dialog) {
        case 'close':
          data = await this.workflowService.getCloseTransition(payeeCaseReferenceId);
          break;
        default:
          data = await this.workflowService.getFullPaymentTransition(payeeCaseReferenceId);
      }

      this.workflowService.openTransitionDialog({
        caseId: data.case_id,
        transitionId: data.transition_id,
        dialog: data.dialog,
      });
    } catch {
      //
    }
  }

  private async openPerformanceClaimResponse(payeeCaseReferenceId: string): Promise<void> {
    const response = await firstValueFrom(this.payeeCasesService.cases);
    const payeeCase = response.data.find(
      (item) => item.payee_case_reference_id === payeeCaseReferenceId,
    );
    const action = payeeCase ? this.performanceClaimResponseAction(payeeCase) : null;
    if (!payeeCase || !action?.dialog) {
      return;
    }

    this.workflowService.openTransitionDialog({
      caseId: payeeCase.id,
      transitionId: action.id,
      dialog: action.dialog,
    });
  }

  private performanceClaimResponseAction(payeeCase: CaseTableItem): CaseTableItemAction | null {
    if (payeeCase.state.action?.dialog === 'HARD_PERFORMANCE_CLAIM_RESPONSE') {
      return payeeCase.state.action;
    }

    for (const group of payeeCase.actions) {
      const action = group.items.find((item) => item.dialog === 'HARD_PERFORMANCE_CLAIM_RESPONSE');
      if (action) {
        return action;
      }
    }

    return null;
  }

  private updateSkeletonDataSource(): void {
    const rowCount = this.skeletonRowCount;
    this.skeletonDataSource.data = Array(rowCount).fill({});
  }

  ngOnDestroy(): void {
    this.destroy.next();
    this.destroy.complete();
    this.payeeCasesService.onDestroy();
  }

  pageChange(event: PageEvent): void {
    this.payeeCasesService.setPagination(event);
  }

  async exportCases(): Promise<void> {
    await this.payeeCasesService.exportCases();
  }

  private setFilterQueryParams(): void {
    this.router.navigate([], {
      queryParams: this.payeeCasesService.getFilters(),
      queryParamsHandling: 'merge',
    });
  }
}
